<template>
  <distribution-card
    title="Distribuição de canais"
    open-new-tab-label="Ver Resultados gerais de campanhas"
    open-new-tab-route="/campanhas/resultados-gerais"
    data-userflow-id="channels-distribution-card"
    :is-pdf="isPdf"
  >
    <template v-slot:distribution-content>
      <div class="distribution-content-container">
        <div v-if="!isPdf" class="header-description">Entenda em quais canais os consumidores receberam disparo de mensagens no período analisado.</div>
        <card-empty-state v-if="isEmpty" height="100%" class="mt-4" />

        <div v-else class="progress-bars">
          <div v-for="(metric, index) in metrics" :key="`bar-${index}`" class="progress-bar-section">
            <div class="label-action-section" :class="index === 'notReceived' && 'mt-3'">
              <span v-if="metric.hasOwnProperty('channel')" class="channel-value" :style="{ color: metric.color }"> {{ metric.channel }}</span>
              <span class="clients-value" :style="{ color: index === 'notReceived' && metric.color }">
                {{ metric.clients | localeString }}
              </span>
              <mf-quick-actions
                v-if="!isPdf"
                :key="`dre-channels-distribution-${index}`"
                :icon-color="index === 'notReceived' ? metric.color : 'cinza-d10'"
                :quick-action-group-id="`dre-channels-distribution-${index}`"
                :short-description="quickActionLabel(index)"
                :long-description="quickActionLabel(index)"
                :segmentation-group="criteriaGroup(dreChannelsDistributionCriteria(analyticsConfiguration._id, periodId, quickActionPeriod, index))"
                tooltip="Algumas divergências podem ser encontradas nos valores das ações rápidas devido ao descadastramento de consumidores durante o período analisado do Faturamento incremental."
              />
              <span v-if="index !== 'notReceived'" class="total-value">/ {{ totalDistribution | localeString }}</span>
              <span v-else class="label-value">{{ metric.label }}</span>
            </div>
            <progress-bar-chart v-if="index !== 'notReceived'" :barColor="metric.color" :textColor="metric.color" :percent="metric.percentage" fill />
          </div>
        </div>
      </div>
    </template>
  </distribution-card>
</template>

<script>
import DistributionCard from './DistributionCard.vue'
import ProgressBarChart from '@/components/ProgressBarChart.vue'
import { localeString } from '@/config/helpers.js'
import { mapGetters, mapState } from 'vuex'
import { criteriaGroup, dreChannelsDistributionCriteria } from '@/components/quick-actions/segmentationGroups.js'
import CardEmptyState from '@/components/CardEmptyState.vue'
export default {
  name: 'ChannelsDistributionCard',
  components: { DistributionCard, ProgressBarChart, CardEmptyState },
  props: {
    periodId: {
      type: String,
      default: '',
    },
    period: {
      type: Array,
      default: () => [],
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),
    ...mapGetters(['dreChannelsDistribution', 'analyticsConfiguration']),
    quickActionPeriod() {
      const lastUpdated = Number(this.lastUpdate?.last_updated)
      const finalDateDre = Number(this.period[1])
      const finalDate = Math.min(lastUpdated, finalDateDre)

      return [this.period[0], finalDate.toString()]
    },
    allowedChannels() {
      let channels = []
      if (this.$permissions.havePagePermission('campanhas/sms')) channels.push('sms')
      if (this.$permissions.havePagePermission('campanhas/email-v2') && this.$getAllowedModules()['email_v2']) channels.push('email')
      if (this.$permissions.havePagePermission('campanhas/whatsapp') && this.$getAllowedModules()['whatsapp']) channels.push('whatsapp')
      if (this.$permissions.havePagePermission('campanhas/aplicativo') && this.$getAllowedModules()['push']) channels.push('push')
      return channels
    },
    totalDistribution() {
      return this.dreChannelsDistribution.total.quantity
    },
    isEmpty() {
      return this.metrics.length === 0
    },
    metrics() {
      if (!this.dreChannelsDistribution.hasOwnProperty('whatsapp')) {
        return []
      }
      const { whatsapp, push, sms, email, notReceived } = this.dreChannelsDistribution
      return {
        ...(this.allowedChannels.includes('whatsapp')
          ? {
              whatsapp: {
                channel: 'Whatsapp',
                color: '#0FC94D',
                percentage: localeString(whatsapp?.percentage ? whatsapp.percentage * 100 : 0),
                clients: whatsapp?.quantity,
              },
            }
          : {}),

        ...(this.allowedChannels.includes('email')
          ? {
              email: {
                channel: 'E-mail',
                color: '#0063C6',
                percentage: localeString(email?.percentage ? email.percentage * 100 : 0),
                clients: email?.quantity,
              },
            }
          : {}),

        ...(this.allowedChannels.includes('sms')
          ? {
              sms: {
                channel: 'SMS',
                color: '#F36600',
                percentage: localeString(sms?.percentage ? sms.percentage * 100 : 0),
                clients: sms?.quantity,
              },
            }
          : {}),

        ...(this.allowedChannels.includes('push')
          ? {
              push: {
                channel: 'Aplicativo',
                color: '#801DE3',
                percentage: localeString(push?.percentage ? push.percentage * 100 : 0),
                clients: push?.quantity,
              },
            }
          : {}),

        notReceived: {
          label: 'Não receberam nenhum disparo',
          color: '#E03210',
          percentage: localeString(notReceived?.percentage ? notReceived.percentage * 100 : 0),
          clients: notReceived?.quantity,
        },
      }
    },
  },
  methods: {
    localeString,
    criteriaGroup,
    dreChannelsDistributionCriteria,
    quickActionLabel(channel) {
      if (channel === 'notReceived') return 'Clientes que não receberam disparo'
      return `Clientes que receberam disparo ${this.metrics[channel].channel}`
    },
  },
}
</script>

<style lang="scss" scoped>
.distribution-content-container {
  .header-description {
    color: $cinza-0;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    width: 100%;
    margin: 15px 0px;
  }
  .progress-bars {
    display: flex;
    flex-direction: column;
    gap: 12px;
    .progress-bar-section {
      height: 41px;
      .label-action-section {
        .channel-value {
          font-size: 16px;
          font-weight: 600;
          line-height: 120%;
          margin-right: 12px;
        }
        .clients-value {
          color: $cinza-d10;
          font-size: 14px;
          font-weight: 700;
          line-height: 120%;
          letter-spacing: 0.14px;
          margin-right: -2px;
        }
        .total-value {
          margin-left: 2px;
          color: $cinza-l30;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
        }
        .label-value {
          margin-top: 12px;
          color: $cinza-d10;
          font-size: 14px;
          font-weight: 400;
          line-height: 120%;
        }
      }
    }
  }
}
</style>
